import React, {useEffect, useState} from 'react';
import { useFormik, FieldArray } from 'formik'; // FieldArray is useful for dynamic arrays, but not strictly needed for fixed size
import * as Yup from 'yup';
import axios from 'axios';
import {getLoggedInUser} from "../utils/functions";
import {NavigateFunction, useNavigate} from "react-router-dom";
import {UserInfo} from "../types/interfaces";

// Define the interfaces for the data structure
interface Document {
    documentNumber: string;
    documentType: string; // Enum could be better, but string is okay for now
    dateOfIssue: string; // Storing as string from input type="date"
    expiryDate: string; // Storing as string from input type="date"
}

interface Address {
    plotWardBox: string;
    cityTown: string;
    addressType: string; // Enum could be better
}

interface FormValues {
    firstName: string;
    lastName: string;
    dateOfBirth: string; // Storing as string from input type="date"
    country: string; // Enum could be better
    sex: string; // Enum (MALE, FEMALE)
    documents: Document[]; // Array of Document objects
    addresses: Address[];   // Array of Address objects
}


interface UpdateSubscriberProps {
    subscriber?: any
}

export const UpdateSubscriberBOCRA = ({subscriber}: UpdateSubscriberProps) => {
    const navigate: NavigateFunction = useNavigate();

    const [apiResponse, setApiResponse] = useState< any| null>(null);
    const [submissionStatus, setSubmissionStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [reponseMessage, setReponseMessage] = useState<string|null>(null);
    const [currentUser, setCurrentUser] = useState<UserInfo|null>(null);

    useEffect(() :void => {
        const getCurrentUser = async () => {
            const loggedInUser: UserInfo | null = await getLoggedInUser()
            if (loggedInUser === null) {
                navigate('/error')
            }
            setCurrentUser(loggedInUser)
        }
        getCurrentUser()

    }, [navigate]);


    const initialValues: FormValues = {
        firstName: subscriber.firstName ||'',
        lastName: subscriber.lastName ||'',
        dateOfBirth: subscriber.dateOfBirth ||'',
        country: 'BOTSWANA', // Default country
        sex: '',
        // Initializing with one empty document and one empty address object
        documents: [{
            documentNumber: subscriber.subscriberDocuments[0]?.documentNumber ||'',
            documentType: 'NATIONAL_ID', // Default document type
            dateOfIssue: subscriber.subscriberDocuments[0]?.dateOfIssue || '',
            expiryDate: subscriber.subscriberDocuments[0]?.expiryDate ||''
        }],
        addresses: [{
            plotWardBox: subscriber.addresses[0]?.plotWardBox ||'',
            cityTown: subscriber.addresses[0]?.cityTown ||'',
            addressType: subscriber.addresses[0].addressType ||'PHYSICAL' // Default address type
        }]
    };

    // Validation schema mirroring the structure with nested validation for array elements
    const validationSchema = Yup.object().shape({
        firstName: Yup.string().required('First name is required'),
        lastName: Yup.string().required('Last name is required'),
        dateOfBirth: Yup.string().required('Date of birth is required'),
        country: Yup.string().required('Country is required'),
        sex: Yup.string().oneOf(['MALE', 'FEMALE'], 'Please select a valid gender').required('Gender is required'),

        // Validation for the documents array
        documents: Yup.array().of(
            Yup.object().shape({
                documentNumber: Yup.string().required('Document number is required'),
                documentType: Yup.string().required('Document type is required'),
                dateOfIssue: Yup.string().required('Date of issue is required'), // Validate as string from input
                expiryDate: Yup.string().required('Expiry date is required')     // Validate as string from input
            }).required('Document details are required') // Ensures each document object is valid
        ).min(1, 'At least one document is required'), // Ensures at least one document entry

        // Validation for the addresses array
        addresses: Yup.array().of(
            Yup.object().shape({
                plotWardBox: Yup.string().required('Plot/Ward/Box is required'),
                cityTown: Yup.string().required('City/Town is required'),
                addressType: Yup.string().required('Address type is required')
            }).required('Address details are required') // Ensures each address object is valid
        ).min(1, 'At least one address is required') // Ensures at least one address entry
    });

    // onSubmit function handles the form submission and data sanitization/formatting
    const onSubmit = async (values: FormValues) => {
        setSubmissionStatus('loading');
        setErrorMessage('');

        try {
            // Sanitize/Format the values for the API payload
            // Convert date strings from the form (YYYY-MM-DD from type="date") to ISO strings
            const formattedValues = {
                ...values,
                id: subscriber.id,
                dateOfBirth: values.dateOfBirth ? new Date(values.dateOfBirth).toISOString() : '',
                documents: values.documents.map(doc => ({
                    ...doc,
                    dateOfIssue: doc.dateOfIssue ? new Date(doc.dateOfIssue).toISOString() : '',
                    expiryDate: doc.expiryDate ? new Date(doc.expiryDate).toISOString() : ''
                })),
                addresses: values.addresses.map(addr => ({
                    ...addr,
                    plotWardBox: addr.plotWardBox.trim(),
                    cityTown: addr.cityTown.trim(),
                }))
            };

            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + currentUser?.token
                }
            }

            const BASE_URL :string = process.env.REACT_APP_API_URL as string;

            const response: any = await axios.patch(BASE_URL+'/updatekyc', formattedValues, config);
            const responseData = response.data;

            if (responseData.success === true) {
                setApiResponse(responseData.data)
                setSubmissionStatus('success');
                //reset form
                formik.resetForm();
            } else {
                setSubmissionStatus('error');
                setErrorMessage(`Submission failed with error: ${response?.data?.message}`);
            }
        } catch (error: any) { // Type the catch variable for better error handling
            console.error("Submission error:", error);
            setSubmissionStatus('error');
            // Provide a more detailed error message if available from the API response
            setErrorMessage(error.response?.data?.message || error.message || 'An error occurred while submitting the form.');
        }
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit
    });

    // Helper function to get error message for array fields
    const getInArrayError = (arrayName: keyof FormValues, index: number, fieldName: string) => {
        // Check if the array itself has been touched and if there's an error for the specific index and field
        const arrayTouched = formik.touched[arrayName] as any;
        const arrayErrors = formik.errors[arrayName] as any;

        return arrayTouched?.[index]?.[fieldName] && arrayErrors?.[index]?.[fieldName]
            ? arrayErrors[index][fieldName]
            : null;
    };


    return (
        <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Personal Information Form</h2>

            <form onSubmit={formik.handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Personal Details</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                                First Name *
                            </label>
                            <input
                                id="firstName"
                                name="firstName" // Formik uses 'name' to link input to state
                                type="text"
                                onChange={formik.handleChange} // Handles updates for individual fields
                                onBlur={formik.handleBlur}   // Handles blur for validation touching
                                value={formik.values.firstName} // Value from Formik state
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border"
                            />
                            {formik.touched.firstName && formik.errors.firstName && (
                                <div className="text-red-500 text-sm mt-1">{formik.errors.firstName}</div>
                            )}
                        </div>

                        <div>
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                                Last Name *
                            </label>
                            <input
                                id="lastName"
                                name="lastName"
                                type="text"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.lastName}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border"
                            />
                            {formik.touched.lastName && formik.errors.lastName && (
                                <div className="text-red-500 text-sm mt-1">{formik.errors.lastName}</div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
                                Date of Birth *
                            </label>
                            <input
                                id="dateOfBirth"
                                name="dateOfBirth"
                                type="date" // HTML5 date input provides basic validation and YYYY-MM-DD format
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.dateOfBirth}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border"
                            />
                            {formik.touched.dateOfBirth && formik.errors.dateOfBirth && (
                                <div className="text-red-500 text-sm mt-1">{formik.errors.dateOfBirth}</div>
                            )}
                        </div>

                        <div>
                            <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                                Country *
                            </label>
                            <input
                                type="text"
                                id="country"
                                name="country"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.country}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border"
                            />
                            {formik.touched.country && formik.errors.country && (
                                <div className="text-red-500 text-sm mt-1">{formik.errors.country}</div>
                            )}
                        </div>

                        {/*<div>*/}
                        {/*    <label htmlFor="country" className="block text-sm font-medium text-gray-700">*/}
                        {/*        Country **/}
                        {/*    </label>*/}
                        {/*    <select*/}
                        {/*        id="country"*/}
                        {/*        name="country"*/}
                        {/*        onChange={formik.handleChange}*/}
                        {/*        onBlur={formik.handleBlur}*/}
                        {/*        value={formik.values.country}*/}
                        {/*        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border"*/}
                        {/*    >*/}
                        {/*        /!* Ensure the option values match the validation schema/API expectations *!/*/}
                        {/*        <option value="">Select Country</option>*/}
                        {/*        <option value="BOTSWANA">Botswana</option>*/}
                        {/*        <option value="SOUTH AFRICA">South Africa</option>*/}
                        {/*        <option value="ZIMBABWE">Zimbabwe</option>*/}
                        {/*        <option value="NAMIBIA">Namibia</option>*/}
                        {/*    </select>*/}
                        {/*    {formik.touched.country && formik.errors.country && (*/}
                        {/*        <div className="text-red-500 text-sm mt-1">{formik.errors.country}</div>*/}
                        {/*    )}*/}
                        {/*</div>*/}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Gender *</label>
                        <div className="mt-1 space-y-2">
                            {['MALE', 'FEMALE'].map((gender) => (
                                <div key={gender} className="flex items-center">
                                    <input
                                        id={`sex-${gender}`}
                                        name="sex" // Radio buttons for the same field share the same name
                                        type="radio"
                                        value={gender}
                                        checked={formik.values.sex === gender} // Checked state linked to Formik value
                                        onChange={formik.handleChange}          // Handle change updates Formik value
                                        className="h-4 w-4 border-gray-300 text-green-600 focus:ring-green-500"
                                    />
                                    <label htmlFor={`sex-${gender}`} className="ml-2 block text-sm text-gray-700 capitalize">
                                        {gender.toLowerCase()}
                                    </label>
                                </div>
                            ))}
                        </div>
                        {formik.touched.sex && formik.errors.sex && (
                            <div className="text-red-500 text-sm mt-1">{formik.errors.sex}</div>
                        )}
                    </div>
                </div>

                {/* Document Information - Iterating over the documents array */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Document Details</h3>

                    {/* Map over the documents array from Formik values */}
                    {formik.values.documents.map((document, index) => (
                        <div key={index} className="space-y-4 border p-4 rounded-lg">
                            <p className="text-sm font-medium text-gray-600">Document {index + 1}</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor={`documents.${index}.documentNumber`} className="block text-sm font-medium text-gray-700">
                                        Document Number *
                                    </label>
                                    <input
                                        disabled
                                        id={`documents.${index}.documentNumber`}
                                        // Naming convention for nested arrays: arrayName.index.fieldName
                                        name={`documents.${index}.documentNumber`}
                                        type="text"
                                        onChange={formik.handleChange} // Formik handles change for this specific field
                                        onBlur={formik.handleBlur}   // Formik handles blur for this specific field
                                        value={document.documentNumber} // Value comes from the object at this index
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border"
                                    />
                                    {/* Accessing errors for nested fields */}
                                    {getInArrayError('documents', index, 'documentNumber') && (
                                        <div className="text-red-500 text-sm mt-1">{getInArrayError('documents', index, 'documentNumber')}</div>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor={`documents.${index}.documentType`} className="block text-sm font-medium text-gray-700">
                                        Document Type *
                                    </label>
                                    <select
                                        id={`documents.${index}.documentType`}
                                        name={`documents.${index}.documentType`}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={document.documentType}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border"
                                    >
                                        <option value="">Select Document Type</option>
                                        <option value="NATIONAL_ID">National ID</option>
                                        <option value="PASSPORT">Passport</option>
                                        <option value="DRIVERS_LICENSE">Driver's License</option>
                                    </select>
                                    {getInArrayError('documents', index, 'documentType') && (
                                        <div className="text-red-500 text-sm mt-1">{getInArrayError('documents', index, 'documentType')}</div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor={`documents.${index}.dateOfIssue`} className="block text-sm font-medium text-gray-700">
                                        Date of Issue *
                                    </label>
                                    <input
                                        id={`documents.${index}.dateOfIssue`}
                                        name={`documents.${index}.dateOfIssue`}
                                        type="date"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={document.dateOfIssue}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border"
                                    />
                                    {getInArrayError('documents', index, 'dateOfIssue') && (
                                        <div className="text-red-500 text-sm mt-1">{getInArrayError('documents', index, 'dateOfIssue')}</div>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor={`documents.${index}.expiryDate`} className="block text-sm font-medium text-gray-700">
                                        Expiry Date *
                                    </label>
                                    <input
                                        id={`documents.${index}.expiryDate`}
                                        name={`documents.${index}.expiryDate`}
                                        type="date"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={document.expiryDate}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border"
                                    />
                                    {getInArrayError('documents', index, 'expiryDate') && (
                                        <div className="text-red-500 text-sm mt-1">{getInArrayError('documents', index, 'expiryDate')}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    {/* Add buttons to add/remove documents here if needed, using Formik's FieldArray */}
                    {/* Example placeholder: <button type="button" onClick={() => ...}>Add Document</button> */}
                </div>

                {/* Address Information - Iterating over the addresses array */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Address Details</h3>

                    {/* Map over the addresses array from Formik values */}
                    {formik.values.addresses.map((address, index) => (
                        <div key={index} className="space-y-4 border p-4 rounded-lg">
                            <p className="text-sm font-medium text-gray-600">Address {index + 1}</p>
                            <div>
                                <label htmlFor={`addresses.${index}.plotWardBox`} className="block text-sm font-medium text-gray-700">
                                    Plot/Ward/Box Number *
                                </label>
                                <input
                                    id={`addresses.${index}.plotWardBox`}
                                    name={`addresses.${index}.plotWardBox`} // Naming convention for nested arrays
                                    type="text"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={address.plotWardBox} // Value from the object at this index
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border"
                                />
                                {/* Accessing errors for nested fields */}
                                {getInArrayError('addresses', index, 'plotWardBox') && (
                                    <div className="text-red-500 text-sm mt-1">{getInArrayError('addresses', index, 'plotWardBox')}</div>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor={`addresses.${index}.cityTown`} className="block text-sm font-medium text-gray-700">
                                        City/Town *
                                    </label>
                                    <input
                                        id={`addresses.${index}.cityTown`}
                                        name={`addresses.${index}.cityTown`}
                                        type="text"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={address.cityTown}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border"
                                    />
                                    {getInArrayError('addresses', index, 'cityTown') && (
                                        <div className="text-red-500 text-sm mt-1">{getInArrayError('addresses', index, 'cityTown')}</div>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor={`addresses.${index}.addressType`} className="block text-sm font-medium text-gray-700">
                                        Address Type *
                                    </label>
                                    <select
                                        id={`addresses.${index}.addressType`}
                                        name={`addresses.${index}.addressType`}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={address.addressType}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border"
                                    >
                                        <option value="">Select Address Type</option>
                                        <option value="PHYSICAL">Physical Address</option>
                                        <option value="POSTAL">Postal Address</option>
                                    </select>
                                    {getInArrayError('addresses', index, 'addressType') && (
                                        <div className="text-red-500 text-sm mt-1">{getInArrayError('addresses', index, 'addressType')}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    {/* Add buttons to add/remove addresses here if needed, using Formik's FieldArray */}
                    {/* Example placeholder: <button type="button" onClick={() => ...}>Add Address</button> */}
                </div>

                {/* Submission Status */}
                {submissionStatus === 'success' && (
                    <div className="p-4 bg-green-50 text-green-700 rounded-lg">
                        Form submitted successfully!
                        {apiResponse && (
                            <div className="mt-2">
                                <p className="font-semibold">Verification Status: {apiResponse.verificationState}</p>
                                {apiResponse.verificationReason && (
                                    <p>Reason: {apiResponse.verificationReason}</p>
                                )}
                            </div>
                        )}
                        <span className="mt-4 block text-red-600 font-bold text-lg">
                            Please update Billing Accordingly!
                        </span>
                    </div>
                )}

                {submissionStatus === 'error' && (
                    <div className="p-4 bg-red-50 text-red-700 rounded-lg">
                        {errorMessage || 'Failed to submit form. Please try again.'}
                    </div>
                )}

                {/* Submit Button */}
                <div className="flex justify-end">
                    <button
                        type="submit"
                        // Disable button while submitting or if form is invalid/not touched
                        disabled={submissionStatus === 'loading' || !formik.isValid || !formik.dirty}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submissionStatus === 'loading' ? 'Submitting...' : 'Submit'}
                    </button>
                </div>
                {/* Optional: Display Formik state for debugging */}
                {/* <pre>{JSON.stringify(formik.values, null, 2)}</pre> */}
            </form>
        </div>
    );
};